import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Field,
  FormRow,
  RightDrawer,
} from '@projexn/ui';
import { type RfiStatus } from '@projexn/shared';
import { api } from '../lib/api';

interface Rfi {
  id: string;
  tenderId: string;
  number: number;
  subject: string;
  question: string;
  answer: string | null;
  status: RfiStatus;
  askedAt: string;
  answeredAt: string | null;
}

interface Tender {
  id: string;
  name: string;
  metadata?: Record<string, unknown>;
}

function refOf(t: Tender): string {
  const r = t.metadata?.reference;
  return typeof r === 'string' ? r : t.id.slice(-6).toUpperCase();
}

const STATUS_TONE: Record<RfiStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  open: 'warning',
  answered: 'info',
  closed: 'success',
  cancelled: 'neutral',
};

export function RfisPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [answering, setAnswering] = useState<Rfi | null>(null);

  const rfisQ = useQuery({
    queryKey: ['rfis'],
    queryFn: () => api.get<{ items: Rfi[] }>('/api/rfis'),
  });
  const tendersQ = useQuery({
    queryKey: ['tenders'],
    queryFn: () => api.get<{ items: Tender[] }>('/api/tenders'),
  });

  const tenderRef = (id: string) => {
    const t = tendersQ.data?.items.find((x) => x.id === id);
    return t ? refOf(t) : '—';
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">RFIs</h1>
          <p className="text-sm text-zinc-500">Requests for Information.</p>
        </div>
        <Button onClick={() => setCreating(true)}>New RFI</Button>
      </header>

      <div className="space-y-2">
        {(rfisQ.data?.items ?? []).map((rfi) => (
          <Card key={rfi.id} className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <code className="text-xs text-zinc-500">
                    {tenderRef(rfi.tenderId)} · RFI #{rfi.number}
                  </code>
                  <Badge tone={STATUS_TONE[rfi.status]}>{rfi.status}</Badge>
                </div>
                <p className="mt-2 text-sm font-medium">{rfi.subject}</p>
                <p className="text-sm text-zinc-700">{rfi.question}</p>
                {rfi.answer && (
                  <p className="mt-2 rounded-md bg-zinc-50 p-2 text-sm text-zinc-700">
                    {rfi.answer}
                  </p>
                )}
              </div>
              {rfi.status === 'open' && (
                <Button variant="secondary" size="sm" onClick={() => setAnswering(rfi)}>
                  Answer
                </Button>
              )}
            </div>
          </Card>
        ))}
        {(rfisQ.data?.items ?? []).length === 0 && (
          <Card className="p-12 text-center text-sm text-zinc-500">No RFIs yet.</Card>
        )}
      </div>

      <RightDrawer open={creating} onClose={() => setCreating(false)} title="New RFI">
        <NewRfiForm
          tenders={tendersQ.data?.items ?? []}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['rfis'] });
          }}
        />
      </RightDrawer>

      <RightDrawer open={!!answering} onClose={() => setAnswering(null)} title="Answer RFI">
        {answering && (
          <AnswerForm
            rfi={answering}
            onAnswered={() => {
              setAnswering(null);
              qc.invalidateQueries({ queryKey: ['rfis'] });
            }}
          />
        )}
      </RightDrawer>
    </div>
  );
}

function NewRfiForm({ tenders, onCreated }: { tenders: Tender[]; onCreated: () => void }) {
  const [tenderId, setTenderId] = useState('');
  const [subject, setSubject] = useState('');
  const [question, setQuestion] = useState('');

  const mutate = useMutation({
    mutationFn: () => api.post('/api/rfis', { tenderId, subject, question }),
    onSuccess: onCreated,
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); mutate.mutate(); }} className="space-y-4">
      <FormRow>
        <Field label="Tender" required>
          <select
            required
            value={tenderId}
            onChange={(e) => setTenderId(e.target.value)}
            className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm"
          >
            <option value="">— select —</option>
            {tenders.map((t) => (
              <option key={t.id} value={t.id}>
                {refOf(t)} · {t.name}
              </option>
            ))}
          </select>
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Subject" required>
          <input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm"
          />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Question" required>
          <textarea
            required
            rows={4}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
          />
        </Field>
      </FormRow>
      {mutate.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {(mutate.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" loading={mutate.isPending}>Submit</Button>
      </div>
    </form>
  );
}

function AnswerForm({ rfi, onAnswered }: { rfi: Rfi; onAnswered: () => void }) {
  const [answer, setAnswer] = useState('');
  const mutate = useMutation({
    mutationFn: () => api.post(`/api/rfis/${rfi.id}/answer`, { answer }),
    onSuccess: onAnswered,
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); mutate.mutate(); }} className="space-y-4">
      <p className="rounded-md bg-zinc-50 p-3 text-sm text-zinc-700">{rfi.question}</p>
      <Field label="Answer" required>
        <textarea
          required
          rows={6}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
        />
      </Field>
      {mutate.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {(mutate.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" loading={mutate.isPending}>Answer</Button>
      </div>
    </form>
  );
}
